import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { LayoutDashboard, FolderKanban, FileCheck, CreditCard, LifeBuoy, LogOut, Menu, Bell, Search, Video, Folder, Settings, X, Briefcase } from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import Chat from '../Chat';
import { apiFetch } from '../../lib/api';
import { usePushNotifications } from '../../hooks/usePushNotifications';

type NavItem = { name: string; path: string; icon: any };
type NotificationItem = { id: string; title: string; text: string; type: string; created_at: string };

export default function PortalLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  usePushNotifications(!!user);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const role = user?.role || 'CLIENT';
  const isEmployee = role === 'EMPLOYEE' || role === 'ADMIN';
  const isPartner = role === 'PARTNER';
  const sectionLabel = isEmployee ? 'Espace Employe' : isPartner ? 'Espace Partenaire' : 'Espace Client';
  const appTitle = isEmployee ? 'Ahime Team' : isPartner ? 'Ahime Partner' : 'Ahime Client';

  const navItems = useMemo<NavItem[]>(() => {
    if (isEmployee) {
      return [
        { name: 'Vue generale', path: '/portal/hr', icon: LayoutDashboard },
        { name: 'Operations', path: '/admin/operations', icon: Briefcase },
        { name: 'Support', path: '/admin/support', icon: LifeBuoy },
        { name: 'Reunions', path: '/admin/meetings', icon: Video },
        { name: 'Documents', path: '/admin/documents', icon: Folder },
        { name: 'Parametres', path: '/portal/hr/settings', icon: Settings },
      ];
    }
    if (isPartner) {
      return [
        { name: 'Vue generale', path: '/portal/prm', icon: LayoutDashboard },
        { name: 'Parametres', path: '/portal/prm/settings', icon: Settings },
      ];
    }
    return [
      { name: "Vue d'ensemble", path: '/portal/client', icon: LayoutDashboard },
      { name: 'Mes Projets', path: '/portal/client/projects', icon: FolderKanban },
      { name: 'Documents', path: '/portal/client/documents', icon: Folder },
      { name: 'Reunions', path: '/portal/client/meetings', icon: Video },
      { name: 'Fichiers & Validation', path: '/portal/client/files', icon: FileCheck },
      { name: 'Facturation', path: '/portal/client/billing', icon: CreditCard },
      { name: 'Support', path: '/portal/client/support', icon: LifeBuoy },
      { name: 'Parametres', path: '/portal/client/settings', icon: Settings },
    ];
  }, [isEmployee, isPartner]);

  useEffect(() => {
    const endpoint = isEmployee ? '/api/dashboard/admin' : `/api/dashboard/client${user?.org_id ? `?org_id=${user.org_id}` : ''}`;
    apiFetch(endpoint)
      .then((res) => res.json())
      .then((data) => setNotifications(Array.isArray(data?.notifications) ? data.notifications : []))
      .catch(() => setNotifications([]));
  }, [location.pathname, user?.org_id, isEmployee]);

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen flex bg-slate-50 font-inter text-slate-900 overflow-hidden">
      {isSidebarOpen && <button onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-slate-900/40 z-20 md:hidden" aria-label="Fermer le menu" />}
      <aside className={clsx("w-72 bg-white border-r border-slate-200 flex flex-col z-30 fixed md:static inset-y-0 left-0 transition-transform duration-300", isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0')}>
        <div className="h-20 flex items-center px-8 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <img src="/favicon-32x32.png" alt="Ahime" width={40} height={40} decoding="async" className="h-10 w-10" />
            <img src="/logo.png" alt={appTitle} width={900} height={255} decoding="async" className="block h-10 w-auto object-contain" />
          </div>
        </div>

        <nav className="flex-1 py-8 px-4 space-y-2 overflow-y-auto custom-scrollbar">
          <p className="px-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">{sectionLabel}</p>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.name} to={item.path} className={clsx('group flex items-center justify-between px-4 py-3.5 rounded-2xl text-sm font-bold transition-all duration-200', isActive ? 'bg-red-50 text-red-700 shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900')}>
                <div className="flex items-center gap-3">
                  <item.icon className={clsx('w-5 h-5 transition-colors', isActive ? 'text-red-600' : 'text-slate-400 group-hover:text-slate-900')} />
                  {item.name}
                </div>
                {isActive && <motion.div layoutId="active-pill-client" className="w-1.5 h-1.5 bg-red-600 rounded-full" />}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-100">
          <div className="bg-slate-50 rounded-[2rem] p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-white text-slate-700 rounded-xl flex items-center justify-center font-bold border border-slate-200 shadow-sm">{user?.email?.charAt(0).toUpperCase()}</div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{user?.email}</p>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{role}</p>
              </div>
            </div>
            <button onClick={handleLogout} className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-bold bg-white border border-slate-200 hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition-all text-slate-500 shadow-sm">
              <LogOut className="w-4 h-4" />
              Deconnexion
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        <header className="h-20 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-10 z-20">
          <div className="flex items-center gap-8 flex-1">
            <button onClick={() => setIsSidebarOpen((v) => !v)} className="md:hidden text-slate-500 hover:text-slate-900">
              <Menu className="w-6 h-6" />
            </button>
            <div className="relative max-w-md w-full hidden md:block group">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-red-600 transition-colors" />
              <input type="text" placeholder="Rechercher..." className="w-full pl-12 pr-4 py-2.5 rounded-xl bg-slate-100 border-transparent focus:bg-white focus:ring-4 focus:ring-red-500/10 focus:border-red-500 outline-none transition-all text-sm font-medium" />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="relative">
              <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 text-slate-400 hover:text-slate-900 transition-colors">
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-600 rounded-full border-2 border-white"></span>}
              </button>
              <AnimatePresence>
                {showNotifications && (
                  <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute right-0 mt-4 w-80 bg-white rounded-[2rem] shadow-2xl border border-slate-100 overflow-hidden z-50">
                    <div className="p-6 border-b border-slate-50 bg-slate-50/50 flex items-center justify-between">
                      <h3 className="font-bold text-slate-900">Notifications</h3>
                      <button onClick={() => setShowNotifications(false)} className="text-slate-400 hover:text-slate-900">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="max-h-96 overflow-y-auto p-4 space-y-2">
                      {notifications.length === 0 && <p className="text-sm text-slate-500 p-2">Aucune notification.</p>}
                      {notifications.map((n) => (
                        <div key={n.id} className="p-4 rounded-2xl hover:bg-slate-50 transition-colors">
                          <p className="font-bold text-sm text-slate-900">{n.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{n.text}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">{new Date(n.created_at).toLocaleString('fr-FR')}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-10 bg-slate-50/50">
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
              {children}
            </motion.div>
          </AnimatePresence>
        </main>

        <Chat room={`${role.toLowerCase()}_${user?.org_id || user?.id || 'public'}`} currentUser={{ name: user?.name || user?.email, email: user?.email }} />
      </div>
    </div>
  );
}
